// Recorte inteligente: uma rede neural (ISNet, licença Apache-2.0) roda aqui no
// navegador e diz, pixel a pixel, o que é produto. Ela não substitui o recorte
// por cor (fotoCatalogo.js), serve de guarda: onde a IA tem certeza que é
// produto, o recorte por cor não apaga. Era isso que deixava assento, lavatório
// e luminária brancos transparentes, "sumindo" no fundo do pôster.
//
// Roda na placa de vídeo (WebGPU, ~1 s por foto) e, sem ela, no processador
// (~10 s). O modelo (178 MB, servido pelo próprio portal em /recorte-ia/) é
// baixado uma vez e fica guardado no navegador; a máscara de cada foto também,
// então reabrir o editor não refaz nada. Qualquer falha devolve null e o
// recorte segue só por cor, como era antes.

const BASE = `${process.env.PUBLIC_URL || ''}/recorte-ia`;
const ORT_URL = `${BASE}/ort/ort.webgpu.min.js`;
const MODELO_URL = `${BASE}/isnet-general-use.onnx`;
const LADO = 1024;                       // entrada fixa do ISNet
const MEDIA = [0.485, 0.456, 0.406];     // normalização do treino (rembg/DIS)
// Trocar de modelo = trocar a versão: as máscaras guardadas eram do anterior.
const CACHE_MODELO = 'recorte-ia-modelo-v1';
const CACHE_MASCARAS = 'recorte-ia-mascaras-v1';

// ---- Estado pra tela (baixando 45%, pronto, indisponível)
let estado = { fase: 'parado' };
const ouvintes = new Set();
function avisar(novo) {
  estado = novo;
  ouvintes.forEach(fn => { try { fn(estado); } catch { /* tela desmontada */ } });
}
export const estadoRecorteIA = () => estado;
export function ouvirRecorteIA(fn) { ouvintes.add(fn); return () => ouvintes.delete(fn); }

function carregarScript(src) {
  return new Promise((ok, falha) => {
    if (window.ort) return ok(window.ort);
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = () => (window.ort ? ok(window.ort) : falha(new Error('o motor não carregou')));
    s.onerror = () => falha(new Error('o motor não carregou'));
    document.head.appendChild(s);
  });
}

async function abrirCache(nome) {
  try { return window.caches ? await caches.open(nome) : null; } catch { return null; }
}

// Apaga os caches de versões anteriores (modelo velho ocupa 178 MB à toa).
async function limparVersoesVelhas() {
  try {
    for (const k of await caches.keys()) {
      if (k.startsWith('recorte-ia-') && k !== CACHE_MODELO && k !== CACHE_MASCARAS) await caches.delete(k);
    }
  } catch { /* sem Cache API: nada guardado */ }
}

async function baixarModelo() {
  const cache = await abrirCache(CACHE_MODELO);
  const guardado = cache && await cache.match(MODELO_URL).catch(() => null);
  if (guardado) return guardado.arrayBuffer();

  const r = await fetch(MODELO_URL);
  if (!r.ok || /text\/html/.test(r.headers.get('content-type') || '')) throw new Error(`modelo indisponível (${r.status})`);
  const total = Number(r.headers.get('content-length')) || 0;
  const leitor = r.body.getReader();
  const partes = [];
  let lidos = 0, ultimo = -1;
  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    partes.push(value);
    lidos += value.length;
    const pct = total ? Math.floor((100 * lidos) / total) : null;
    if (pct !== ultimo) { ultimo = pct; avisar({ fase: 'baixando', pct, mb: Math.round(lidos / 1048576) }); }
  }
  const buf = new Uint8Array(lidos);
  let o = 0;
  for (const p of partes) { buf.set(p, o); o += p.length; }
  if (cache) await cache.put(MODELO_URL, new Response(buf, { headers: { 'Content-Type': 'application/octet-stream' } })).catch(() => {});
  return buf.buffer;
}

async function criarSessao(soProcessador) {
  avisar({ fase: 'carregando' });
  const ort = await carregarScript(ORT_URL);
  ort.env.wasm.wasmPaths = `${BASE}/ort/`;
  limparVersoesVelhas();
  const modelo = await baixarModelo();
  avisar({ fase: 'carregando' });
  let gpu = false;
  if (!soProcessador && navigator.gpu) gpu = !!(await navigator.gpu.requestAdapter().catch(() => null));
  // No processador o cálculo vai pra uma thread à parte; senão a tela congela uns
  // segundos a cada foto. (A placa de vídeo não trava a tela e não aceita isso.)
  if (!gpu && !window.__ortIniciado) ort.env.wasm.proxy = true;
  window.__ortIniciado = true;
  if (gpu) {
    try {
      const s = await ort.InferenceSession.create(modelo, { executionProviders: ['webgpu'] });
      avisar({ fase: 'pronto', ep: 'webgpu' });
      return { ort, s, ep: 'webgpu' };
    } catch (e) { console.warn('[recorte-ia] placa de vídeo recusou, indo pro processador:', e); }
  }
  const s = await ort.InferenceSession.create(modelo, { executionProviders: ['wasm'] });
  avisar({ fase: 'pronto', ep: 'wasm' });
  return { ort, s, ep: 'wasm' };
}

let sessaoP = null;
function sessao(soProcessador = false) {
  if (!sessaoP) {
    sessaoP = criarSessao(soProcessador).catch((e) => {
      console.warn('[recorte-ia] indisponível, seguindo com o recorte por cor:', e);
      avisar({ fase: 'falhou' });
      return null;
    });
  }
  return sessaoP;
}

// Uma inferência por vez (a fila de fotos trata duas ao mesmo tempo).
let fila = Promise.resolve();
const emFila = (fn) => { const r = fila.then(fn, fn); fila = r.catch(() => {}); return r; };

/** Máscara 1024×1024 (0–255) da foto, ou null. */
async function inferir(img) {
  let ses = await sessao();
  if (!ses) return null;
  const c = document.createElement('canvas');
  c.width = c.height = LADO;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, LADO, LADO);
  const d = ctx.getImageData(0, 0, LADO, LADO).data;
  const N = LADO * LADO;
  const entrada = new Float32Array(3 * N);
  for (let i = 0; i < N; i++) {
    entrada[i] = d[i * 4] / 255 - MEDIA[0];
    entrada[N + i] = d[i * 4 + 1] / 255 - MEDIA[1];
    entrada[2 * N + i] = d[i * 4 + 2] / 255 - MEDIA[2];
  }
  const rodar = async ({ ort, s }) => {
    // O modelo tem 12 saídas (uma por escala); só a primeira, a final, interessa.
    const saida = await s.run({ [s.inputNames[0]]: new ort.Tensor('float32', entrada, [1, 3, LADO, LADO]) }, [s.outputNames[0]]);
    return saida[s.outputNames[0]].data;
  };
  let o;
  try { o = await rodar(ses); } catch (e) {
    if (ses.ep !== 'webgpu') throw e;
    // Placa de vídeo que abre a sessão mas falha ao rodar: refaz no processador.
    console.warn('[recorte-ia] falhou na placa de vídeo, indo pro processador:', e);
    ses.s.release?.().catch?.(() => {});
    sessaoP = null;
    ses = await sessao(true);
    if (!ses) return null;
    o = await rodar(ses);
  }
  // A saída já vem de 0 a 1 (chance de ser produto).
  const m = new Uint8ClampedArray(N);
  for (let i = 0; i < N; i++) m[i] = o[i] * 255;
  return m;
}

// Máscara 1024² <-> PNG cinza (pra guardar no navegador)
function mascaraEmCanvas(m) {
  const c = document.createElement('canvas');
  c.width = c.height = LADO;
  const ctx = c.getContext('2d');
  const im = ctx.createImageData(LADO, LADO);
  for (let i = 0; i < m.length; i++) { const k = i * 4; im.data[k] = im.data[k + 1] = im.data[k + 2] = m[i]; im.data[k + 3] = 255; }
  ctx.putImageData(im, 0, 0);
  return c;
}

// Impressão digital dos bytes da foto (a mesma foto em outra URL temporária
// acha a máscara guardada).
const digitais = new Map();   // src -> Promise<hex | null>
function digitalDaFoto(src) {
  if (!digitais.has(src)) {
    digitais.set(src, (async () => {
      if (!window.crypto?.subtle) return null;
      try {
        const bytes = await (await fetch(src)).arrayBuffer();
        const h = new Uint8Array(await crypto.subtle.digest('SHA-1', bytes));
        return Array.from(h, b => b.toString(16).padStart(2, '0')).join('');
      } catch { return null; }
    })());
  }
  return digitais.get(src);
}

// A 2ª olhada vem da foto com a 1ª máscara apagada (fotoCatalogo.js): mudou
// esse jeito de apagar, troca o sufixo, senão volta máscara velha.
async function chaveDaFoto(src, olhada) {
  const h = await digitalDaFoto(src);
  return h && `${BASE}/mascara/${h}${olhada > 1 ? `-${olhada}` : ''}.png`;
}

async function mascaraGuardada(chave) {
  const cache = chave && await abrirCache(CACHE_MASCARAS);
  const r = cache && await cache.match(chave).catch(() => null);
  if (!r) return null;
  try {
    const bmp = await createImageBitmap(await r.blob());
    const c = document.createElement('canvas');
    c.width = c.height = LADO;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const d = ctx.getImageData(0, 0, LADO, LADO).data;
    const m = new Uint8ClampedArray(LADO * LADO);
    for (let i = 0; i < m.length; i++) m[i] = d[i * 4];
    return m;
  } catch { return null; }
}

async function guardarMascara(chave, m) {
  const cache = chave && await abrirCache(CACHE_MASCARAS);
  if (!cache) return;
  const blob = await new Promise(ok => mascaraEmCanvas(m).toBlob(ok, 'image/png'));
  if (blob) await cache.put(chave, new Response(blob, { headers: { 'Content-Type': 'image/png' } })).catch(() => {});
}

const porFoto = new Map();   // "src#olhada" -> Promise<máscara 1024² | null> (1 MB cada)

/**
 * Máscara de produto da foto no tamanho dela (Uint8, 0 = fundo, 255 = produto),
 * ou null se o recorte inteligente não estiver disponível.
 * `src` é a URL de onde a imagem veio: identifica a foto pro cache.
 * `olhada` 2: `img` é a foto com a 1ª máscara apagada (canvas do mesmo tamanho).
 */
export async function mascaraIA(img, src, olhada = 1) {
  const W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
  const k = `${src}#${olhada}`;
  if (!porFoto.has(k)) {
    porFoto.set(k, (async () => {
      const chave = await chaveDaFoto(src, olhada);
      const guardada = await mascaraGuardada(chave);
      if (guardada) return guardada;
      const m = await emFila(() => inferir(img));
      if (m) guardarMascara(chave, m);
      return m;
    })().catch((e) => { console.warn('[recorte-ia] máscara falhou:', e); return null; }));
  }
  const m1024 = await porFoto.get(k);
  if (!m1024) { porFoto.delete(k); return null; }

  // Volta pro tamanho da foto
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(mascaraEmCanvas(m1024), 0, 0, W, H);
  const d = ctx.getImageData(0, 0, W, H).data;
  const m = new Uint8Array(W * H);
  for (let p = 0; p < m.length; p++) m[p] = d[p * 4];
  return m;
}

/** Esquece a máscara em memória de uma foto (a URL temporária foi liberada). */
export function esquecerFoto(src) {
  porFoto.delete(`${src}#1`);
  porFoto.delete(`${src}#2`);
  digitais.delete(src);
}
